import React, { createContext, useContext, useState, ReactNode } from "react";
import {
  IfIsaProviderIsaType,
  CreateIfIsaTransferDto,
} from "@ifgengineering/client-invest-sdk";

export type TransferStep =
  | "deal-selection"
  | "isa-manager-selection"
  | "isa-type-selection"
  | "selected-isa-type-input"
  | "isa-summary"
  | "notice-period"
  | "authorisation"
  | "declaration"
  | "confirmation";

// Helper type for ISA summary step info
export type IsaSummaryStepInfo = {
  selectedIsaTypes: IfIsaProviderIsaType[];
};

export type StepMetadata = {
  title: string;
  description: string;
};

interface TransferNavigationContextType {
  currentStep: TransferStep;
  completedSteps: Set<TransferStep>;
  totalSteps: number;
  selectedIsaTypes: IfIsaProviderIsaType[];
  setSelectedIsaTypes: (types: IfIsaProviderIsaType[]) => void;
  setCurrentStep: (step: TransferStep) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  markStepCompleted: (step: TransferStep) => void;
  resetFlow: () => void;
  getDynamicSteps: () => TransferStep[];
  currentStepMetadata: StepMetadata;
  setCurrentStepMetadata: (metadata: StepMetadata) => void;
  transferData: Partial<CreateIfIsaTransferDto & { dealName?: string }>;
  updateTransferData: (
    data: Partial<CreateIfIsaTransferDto & { dealName?: string }>
  ) => void;
  getTransferData: () => CreateIfIsaTransferDto & { dealName?: string };
  customNextHandler: (() => void) | null;
  setCustomNextHandler: (handler: (() => void) | null) => void;
  customNextDisabled: boolean;
  setCustomNextDisabled: (disabled: boolean) => void;
  setSignatureUri: (signatureUri?: string) => void;
  signatureUri?: string;
}

const TransferNavigationContext = createContext<
  TransferNavigationContextType | undefined
>(undefined);

interface TransferNavigationProviderProps {
  children: ReactNode;
  initialStep?: TransferStep;
}

export const TransferNavigationProvider: React.FC<
  TransferNavigationProviderProps
> = ({ children, initialStep = "deal-selection" }) => {
  const [currentStep, setCurrentStep] = useState<TransferStep>(initialStep);
  const [completedSteps, setCompletedSteps] = useState<Set<TransferStep>>(
    new Set()
  );
  const [selectedIsaTypes, setSelectedIsaTypes] = useState<
    IfIsaProviderIsaType[]
  >([]);
  const [customNextHandler, setCustomNextHandler] = useState<
    (() => void) | null
  >(null);
  const [customNextDisabled, setCustomNextDisabled] = useState<boolean>(false);
  const [transferData, setTransferData] = useState<
    Partial<CreateIfIsaTransferDto>
  >({
    transactions: [],
    currentManagerName: "",
  });
  const [signatureUri, setSignatureUri] = useState<string | undefined>();
  const [currentStepMetadata, setCurrentStepMetadata] = useState<StepMetadata>({
    title: "",
    description: "",
  });

  const getDynamicSteps = (): TransferStep[] => {
    const steps: TransferStep[] = [
      "deal-selection",
      "isa-manager-selection",
      "isa-type-selection",
      "selected-isa-type-input",
      "isa-summary",
      "notice-period",
      "authorisation",
      "declaration",
    ];

    return steps;
  };

  const dynamicSteps = getDynamicSteps();
  const totalSteps = dynamicSteps.length;

  const goToNextStep = () => {
    const currentIndex = dynamicSteps.indexOf(currentStep);
    if (currentIndex < dynamicSteps.length - 1) {
      const nextStep = dynamicSteps[currentIndex + 1];
      setCompletedSteps((prev) => new Set([...prev, currentStep]));
      setCurrentStep(nextStep);
    }
  };

  const goToPreviousStep = () => {
    const currentIndex = dynamicSteps.indexOf(currentStep);
    if (currentIndex > 0) {
      const previousStep = dynamicSteps[currentIndex - 1];
      setCompletedSteps((prev) => {
        const newSet = new Set(prev);
        newSet.delete(currentStep);
        return newSet;
      });
      setCurrentStep(previousStep);
    }
  };

  const markStepCompleted = (step: TransferStep) => {
    setCompletedSteps((prev) => new Set([...prev, step]));
  };

  const resetFlow = () => {
    setCurrentStep(initialStep);
    setCompletedSteps(new Set());
    setSelectedIsaTypes([]);
    setTransferData({
      transactions: [],
      currentManagerName: "",
    });
  };

  const updateTransferData = (data: Partial<CreateIfIsaTransferDto>) => {
    setTransferData((prev) => ({ ...prev, ...data }));
  };

  const getTransferData = (): CreateIfIsaTransferDto => {
    return {
      dealId: transferData.dealId || 0,
      currentManagerName: transferData.currentManagerName || "",
      currentManagerAddress: transferData.currentManagerAddress,
      transactions: transferData.transactions || [],
      noticeDecision: transferData.noticeDecision || "SERVE_NOTICED",
    };
  };

  const value: TransferNavigationContextType = {
    currentStep,
    completedSteps,
    totalSteps,
    selectedIsaTypes,
    setSelectedIsaTypes,
    setCurrentStep,
    goToNextStep,
    goToPreviousStep,
    markStepCompleted,
    resetFlow,
    getDynamicSteps,
    transferData,
    updateTransferData,
    getTransferData,
    currentStepMetadata,
    setCurrentStepMetadata,
    customNextHandler,
    setCustomNextHandler,
    customNextDisabled,
    setCustomNextDisabled,
    setSignatureUri,
    signatureUri,
  };

  return (
    <TransferNavigationContext.Provider value={value}>
      {children}
    </TransferNavigationContext.Provider>
  );
};

export const useTransferNavigation = (): TransferNavigationContextType => {
  const context = useContext(TransferNavigationContext);
  if (context === undefined) {
    throw new Error(
      "useTransferNavigation must be used within a TransferNavigationProvider"
    );
  }
  return context;
};
